<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { workLogsApi } from '$lib/api/workLogs';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import { t } from '$lib/i18n';
  import type { WorkLog } from '$lib/types';

  let page = $state(1);
  const perPage = 20;

  const query = createQuery(() => ({
    queryKey: ['work-logs', page],
    queryFn: () => workLogsApi.list(page, perPage)
  }));

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString();
  }
  function fmtTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  function duration(l: WorkLog): string {
    if (!l.punch_out) return t('history.inProgress');
    const hrs = (new Date(l.punch_out).getTime() - new Date(l.punch_in).getTime()) / 3_600_000;
    return `${hrs.toFixed(1)}h`;
  }
</script>

<div class="tym-page">
  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('history.error')}</div>
  {:else if query.data}
    <DataTable
      mobileCards
      mobileColumns={['date', 'in', 'out', 'duration', 'site']}
      columns={[
        { key: 'date', label: t('history.date'), cell: dateCell },
        { key: 'in', label: t('history.in'), cell: inCell },
        { key: 'out', label: t('history.out'), cell: outCell },
        { key: 'duration', label: t('history.duration'), cell: durationCell },
        { key: 'site', label: t('history.site'), cell: siteCell },
        { key: 'tasks', label: t('history.tasks'), cell: tasksCell }
      ]}
      rows={query.data.work_logs}
      rowKey={(l) => l.id}
      emptyTitle={t('history.empty')}
      footer={query.data.total > 0 ? footer : undefined}
    />
  {/if}
</div>

{#snippet dateCell(l: WorkLog)}
  {fmtDate(l.punch_in)}
{/snippet}
{#snippet inCell(l: WorkLog)}
  {fmtTime(l.punch_in)}
{/snippet}
{#snippet outCell(l: WorkLog)}
  <span class="muted">{l.punch_out ? fmtTime(l.punch_out) : '—'}</span>
{/snippet}
{#snippet durationCell(l: WorkLog)}
  {duration(l)}
{/snippet}
{#snippet siteCell(l: WorkLog)}
	{#if l.manual_attendance}
	  <span class="tym-badge tym-badge--info">HR-entered · not GPS verified</span>
	{/if}
  <!-- A remote punch has no work zone — it carries its pasted place instead. -->
  {#if l.remote}
    <span class="tym-badge tym-badge--info">{t('history.remote')}</span>
    {#if l.maps_link}
      <a class="ml-1 text-toyama-teal underline" href={l.maps_link} target="_blank" rel="noopener noreferrer">
        {l.location_name}
      </a>
    {:else}
      <span class="ml-1">{l.location_name}</span>
    {/if}
    {#if l.location_area && l.location_area !== '—'}
      <span class="muted"> · {l.location_area}</span>
    {/if}
  {:else}
    <span class="muted">{l.work_zone_name ?? '—'}</span>
  {/if}
{/snippet}
{#snippet tasksCell(l: WorkLog)}
  <span class="muted">{l.tasks?.length ?? 0}</span>
{/snippet}
{#snippet footer()}
  <Pagination {page} perPage={20} count={query.data?.total ?? 0} onChange={(p) => (page = p)} />
{/snippet}
